import { RequestContext, safeBool, Domain, QueryParameters } from 'DVWebNodeUtils'
import { isRetail as isRetailSite } from '../utils/domainUtils'
import { createSelector } from './selectorWrapper'
import { Omit } from '../utils/utils'
import { MakeUrlArgs, makeUrl } from '../utils/url'
import { simpleSelectors } from './basicState'

export interface IRequestContext {
  rawContext: RequestContext
  isInternal: boolean
  sessionID: string
  requestID: string
  isRetail: boolean
  isRecognizedCustomer: boolean
  originalURI: string
  queryParameters: QueryParameters
  makeUrl: MakeUrl
  locale: string
}

export type MakeUrl = (
  path: string,
  params: Omit<MakeUrlArgs, 'urlData'>
) => string

export const domainSelector = createSelector(
  simpleSelectors.context,
  (context: RequestContext) => context.domain || 'Fogg'
)

export const sessionIdSelector = createSelector(
  simpleSelectors.context,
  (context: RequestContext) => context.sessionID
)

export const requestIdSelector = createSelector(
  simpleSelectors.context,
  (context: RequestContext) => context.requestID
)

export const customerIdSelector = createSelector(
  simpleSelectors.context,
  (context: RequestContext) => context.customerID
)

export const localeSelector = createSelector(
  simpleSelectors.context,
  (context: RequestContext) => context.locale || 'en_US'
)

export const numberLocaleSelector = createSelector(
  localeSelector,
  (locale: string) => locale.replace('_', '-')
)

export const isInternalSelector = createSelector(
  simpleSelectors.context,
  (context: RequestContext) => safeBool(context.isInternal || '0')
)

export const queryParametersSelector = createSelector(
  simpleSelectors.context,
  (context: RequestContext) => context.queryParameters || {}
)

export const originalURISelector = createSelector(
  simpleSelectors.context,
  (context: RequestContext) => context.originalURI || ''
)

export const marketplaceIdSelector = createSelector(
  simpleSelectors.context,
  (context: RequestContext) => context.marketplaceID
)

export const isRetailSelector = createSelector(
  domainSelector,
  (domain: Domain) => isRetailSite(domain)
)

export const urlMakerSelector = createSelector(
  [isRetailSelector, originalURISelector, queryParametersSelector],
  (isRetail, originalURI, parameters) => (path: string, args: MakeUrlArgs) =>
    makeUrl(path, { urlData: { isRetail, originalURI, parameters }, ...args })
)

export const sanitizedRequestContextSelector = createSelector(
  [
    simpleSelectors.context,
    isRetailSelector,
    originalURISelector,
    queryParametersSelector,
    isInternalSelector,
    sessionIdSelector,
    requestIdSelector,
    numberLocaleSelector,
    urlMakerSelector,
    customerIdSelector
  ],
  (
    rawContext: RequestContext,
    isRetail: boolean,
    originalURI: string,
    queryParameters: QueryParameters,
    isInternal: boolean,
    sessionID: string,
    requestID: string,
    locale: string,
    makeUrl: MakeUrl,
    customerId?: string
  ): IRequestContext => {
    return {
      rawContext,
      isRetail,
      originalURI,
      queryParameters,
      isInternal,
      makeUrl,
      sessionID,
      isRecognizedCustomer: Boolean(customerId),
      requestID,
      locale
    }
  }
)
